import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button, ListGroup, ListGroupItem } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamErrorMessage } from './teamErrorMessage';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

interface PendingInvite {
  id: number;
  email: string;
  createdAt: Date;
  expiresAt: Date;
}

interface TeamPendingInvitesProps {
  invites: PendingInvite[];
  onRevoke: (inviteId: number) => void;
  revokeError: unknown;
  isRevoking: boolean;
}

const TeamPendingInvitesComponent: React.FC<TeamPendingInvitesProps> = ({
  invites,
  onRevoke,
  revokeError,
  isRevoking,
}) => {
  const intl = useIntl();

  const rows = useMemo(
    () =>
      invites.map((invite) => (
        <ListGroupItem key={invite.id} className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span data-clarity-mask="true">{invite.email}</span>
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted small">
              {intl.formatMessage(
                { id: 'app.sml.team.expires', defaultMessage: 'Expires {date}' },
                { date: intl.formatDate(invite.expiresAt, { dateStyle: 'medium' }) }
              )}
            </span>
            <Button color="link" className="p-0" disabled={isRevoking} onClick={() => onRevoke(invite.id)}>
              {intl.formatMessage({ id: 'app.sml.team.revoke', defaultMessage: 'Cancel invite' })}
            </Button>
          </div>
        </ListGroupItem>
      )),
    [invites, isRevoking, onRevoke, intl]
  );

  const hasError = revokeError !== null && revokeError !== undefined;
  if (invites.length === 0 && !hasError) {
    return null;
  }

  return (
    <Panel as="section" className="mb-4">
      <PanelHead title={intl.formatMessage({ id: 'app.sml.team.pending', defaultMessage: 'Pending invites' })} />
      <PanelBody>
        <ListGroup flush>{rows}</ListGroup>
        {hasError && (
          <Alert color="danger" className="mt-3 mb-0">
            {teamErrorMessage(intl, revokeError)}
          </Alert>
        )}
      </PanelBody>
    </Panel>
  );
};

export const TeamPendingInvites = TypedMemo(TeamPendingInvitesComponent);
